import React, { useEffect, useState, useMemo } from "react";
import Chart from "../../Chart";
import * as FeatherIcon from "react-feather";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { getUrlDetailsObj, getUserStatsObject, recordActivity, smartDecimalNumberFormatter } from "../../../services/commonService";
import { useSelector } from "react-redux";
import BarGraphSkeleton from "../skeletons/barGraphSkeleton/barGraphSkeleton";
import { useUser } from "../../../context/UserContext";

const formatFinancialData = (data) => {
  if (!data) return [];
  const sortedData = data.sort((a, b) => Number(a.category) - Number(b.category));
  return sortedData.map(item => ({
    ...item,
    value: item.value == null ? null : smartDecimalNumberFormatter(item.value)
  }));
};

export default function KeyFinancials() {
  const {user} = useUser()
  const { accountId, accountName, clientName } = useParams();
  const [graph, setGraph] = useState('revenue');
  const navigate = useNavigate();
  const currentUrl = useLocation().pathname;
  const pageParsedData = getUrlDetailsObj(useLocation, useParams);
  const { financialsData, loading } = useSelector(state => state.companyFinancialsReducer);

  const financials = useMemo(() => formatFinancialData(financialsData?.data?.financials), [loading]);
  const grossProfit = useMemo(() => formatFinancialData(financialsData?.data?.gross_profit), [loading]);
  const ebitDa = useMemo(() => formatFinancialData(financialsData?.data?.ebitda), [loading]);
  const netIncome = useMemo(() => formatFinancialData(financialsData?.data?.net_income), [loading]);

  const lastYerFinValue = useMemo(() => financialsData?.last_year_financial?.value, [loading]);
  const finGrowth = useMemo(() => financialsData?.data?.growth, [loading]);

  const isRevenuesNan = useMemo(() => financials.every(item => isNaN(Number(item.value))), [financials]);
  const isGrossProfitsNan = useMemo(() => grossProfit.every(item => isNaN(Number(item.value))), [grossProfit]);
  const isEbitDasNan = useMemo(() => ebitDa.every(item => isNaN(Number(item.value))), [ebitDa]);
  const isNetIncomesNan = useMemo(() => netIncome.every(item => isNaN(Number(item.value))), [netIncome]);
  
  useEffect(() => {
    if(!user?.id || !graph || !accountName){
      return 
    }
    const graph_tab = {
      revenue: "Revenue",
      gross_profit: "Gross Profit",
      ebitda: "EBITDA",
      net_income: "Net Income"
    }[graph];

    const userStat = getUserStatsObject(
      "Overview",
      "page_load",
      `Page Source: ${accountName}, Key Financials Graph Tab: ${graph_tab}`,
      currentUrl
    );
    userStat.url_details = pageParsedData;
    recordActivity(userStat);
  }, [graph, accountName, user?.id]);

  const handleGraphChange = (graphName) => {
    setGraph(graphName);
  };

  const handleViewMoreFinanceLink = (e) => {
    e.preventDefault();
    const userStat = getUserStatsObject(
      "Overview",
      "View More Click",
      `Page Source: ${accountName}, Key Financials`,
      currentUrl
    );
    userStat.url_details = pageParsedData;
    recordActivity(userStat);

    navigate(`/account_details/${accountId}/${encodeURIComponent(accountName)}/${encodeURIComponent(clientName)}/?tab=more&subTab=financial`);
  };

  const renderChart = (data, isDataNan) => {
    if (data.length > 0 && !isDataNan) {
      return <Chart financials_data={data?.map((item)=>({...item,value:Math.round(item?.value)}))} active_chart={graph} />;
    }
    return null;
  };
  
  if (!loading && !financialsData?.data) {
    return null
  }

  return (
    <>

        <div className="generic-take-space-child mb-2">
          <div className="custom_card_block" >
           {loading ? <BarGraphSkeleton/>:( <div className="key_finance_wrapper">
              <div className="head_top">
                <h5 className="main_head">Key Financials (Last 5 Years $ Mn)</h5>
                <Link onClick={handleViewMoreFinanceLink} className="custom_view_all">View All</Link>
              </div>

              <div className="graph_section">
                <div className="bn_value d-none">
                  {lastYerFinValue && (
                    <p className="bn_value_text">
                      $ {lastYerFinValue}
                      {finGrowth != "" && finGrowth != null && !isNaN(finGrowth) && isFinite(finGrowth) && (
                        <>
                          <span className={finGrowth > 0 ? "arrow_value_green" : "arrow_value_red"}>
                            {finGrowth > 0 ? <FeatherIcon.Plus className="fa" /> : <FeatherIcon.Minus className="fa" />}
                          </span>
                          <span className={finGrowth > 0 ? "value_green" : "value_red"}>
                            {Math.abs(finGrowth)} %
                          </span>
                        </>
                      )}
                    </p>
                  )}
                </div>

                <div className="key_graph_tab">
                  {!isRevenuesNan && <span onClick={() => handleGraphChange('revenue')} className={`key_graph_btn ${graph === 'revenue' ? 'active_tab_kf' : ''}`}>Revenue</span>}
                  {grossProfit?.length > 0 && !isGrossProfitsNan && <span onClick={() => handleGraphChange('gross_profit')} className={`key_graph_btn ${graph === 'gross_profit' ? 'active_tab_kf' : ''}`}>Gross Profit</span>}
                  {ebitDa?.length > 0 && !isEbitDasNan && <span onClick={() => handleGraphChange('ebitda')} className={`key_graph_btn ${graph === 'ebitda' ? 'active_tab_kf' : ''}`}>EBITDA</span>}
                  {netIncome?.length > 0 && !isNetIncomesNan && <span onClick={() => handleGraphChange('net_income')} className={`key_graph_btn ${graph === 'net_income' ? 'active_tab_kf' : ''}`}>Net Income</span>}
                </div>

                {graph === 'revenue' && renderChart(financials, isRevenuesNan)}
                {graph === 'gross_profit' && renderChart(grossProfit, isGrossProfitsNan)}
                {graph === 'ebitda' && renderChart(ebitDa, isEbitDasNan)}
                {graph === 'net_income' && renderChart(netIncome, isNetIncomesNan)}
              </div>
            </div>)}
          </div>
        </div>

    </>
  );
}